import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { CredentialsService } from '../../services/credentials.service';
import {
  CredentialDashboardCards,
  CredentialTypeCard,
} from '../../types/credential-dashboard-cards.type';
import { CredentialMonthlySummary } from '../../types/credential-dashboard-summary.type';

// PrimeNG imports
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { ChartModule } from 'primeng/chart';
import { ToastModule } from 'primeng/toast';
import { DividerModule } from 'primeng/divider';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    InputNumberModule,
    ChartModule,
    ToastModule,
    DividerModule,
  ],
  template: `
    <p-toast></p-toast>

    <div class="dashboard-container">
      <!-- Header -->
      <div class="dashboard-header">
        <h1 class="dashboard-title">
          <i class="pi pi-chart-bar mr-2"></i>Dashboard de Credenciais
        </h1>
        <div class="header-actions">
          <p-button
            label="Atualizar"
            icon="pi pi-refresh"
            severity="secondary"
            [outlined]="true"
            (onClick)="loadDashboardData()"
            [disabled]="loading"
          >
          </p-button>
        </div>
      </div>

      <!-- Filtros de Período -->
      <p-card styleClass="filters-card">
        <ng-template pTemplate="header">
          <div class="filters-header">
            <i class="pi pi-calendar mr-2"></i>
            <h3>Filtros de Período</h3>
          </div>
        </ng-template>

        <div class="filters-container">
          <div class="filter-group">
            <label for="anoInicio" class="filter-label">Ano Inicial:</label>
            <p-inputNumber
              id="anoInicio"
              [(ngModel)]="anoInicio"
              [min]="minYear"
              [max]="getCurrentYear()"
              [useGrouping]="false"
              [showButtons]="true"
              buttonLayout="stacked"
              incrementButtonIcon="pi pi-angle-up"
              decrementButtonIcon="pi pi-angle-down"
              [step]="1"
              (onInput)="validarAnoInicio()"
              placeholder="Ex: 2025"
              styleClass="ano-input ano-inicio"
            >
            </p-inputNumber>
          </div>

          <div class="filter-group">
            <label for="anoFim" class="filter-label">Ano Final:</label>
            <p-inputNumber
              id="anoFim"
              [(ngModel)]="anoFim"
              [min]="minYear"
              [max]="getCurrentYear()"
              [useGrouping]="false"
              [showButtons]="true"
              buttonLayout="stacked"
              incrementButtonIcon="pi pi-angle-up"
              decrementButtonIcon="pi pi-angle-down"
              [step]="1"
              (onInput)="validarAnoFinal()"
              placeholder="Ex: 2026"
              styleClass="ano-input ano-final"
            >
            </p-inputNumber>
          </div>

          <div class="filter-actions">
            <p-button
              label="Filtrar"
              icon="pi pi-filter"
              (onClick)="aplicarFiltros()"
              [disabled]="!anoInicio || !anoFim || loading"
            >
            </p-button>
           <p-button
              label="Limpar"
              icon="pi pi-times"
              severity="secondary"
              [outlined]="true"
              (onClick)="limparFiltros()"
              [disabled]="loading"
            >
            </p-button>
          </div>
        </div>

        <div *ngIf="anoInicio && anoFim" class="periodo-info">
         
          <div class="periodo-display">
            <i class="pi pi-info-circle mr-2"></i>
            <span
              ><strong>Período ativo:</strong> {{ activeStartYear }} a
              {{ activeEndYear }}</span
            >
            <span class="periodo-range">({{ getPeriodInMonths() }} meses)</span>
          </div>
        </div>
      </p-card>

      <!-- Cards de Resumo -->
      <div class="summary-cards" *ngIf="dashboardCards">
        <p-card styleClass="summary-card">
          <ng-template pTemplate="header">
            <div class="card-header">
              <h3>Total Geral</h3>
            </div>
          </ng-template>
          <div class="card-content">
            <i
              class="pi pi-id-card card-icon"
              style="color: var(--primary-color)"
            ></i>
            <div class="card-info">
              <span class="card-label">Credenciais Emitidas</span>
              <span class="card-value">{{ dashboardCards.totalGeral }}</span>
            </div>
          </div>
        </p-card>

        <p-card styleClass="summary-card">
          <ng-template pTemplate="header">
            <div class="card-header">
              <h3>Total TEA</h3>
            </div>
          </ng-template>
          <div class="card-content">
            <img
              [src]="'/assets/images/tea.png'"
              width="40"
              height="40"
              style="margin-right: 5px; border-radius: 5px;"
            />
            <div class="card-info">
              <span class="card-label">Credenciais TEA</span>
              <span class="card-value">{{ dashboardCards.totalTea }}</span>
            </div>
          </div>
        </p-card>

        <p-card styleClass="summary-card">
          <ng-template pTemplate="header">
            <div class="card-header">
              <h3>Tipos de Credencial</h3>
            </div>
          </ng-template>
          <div>
            <div
              *ngFor="let tipo of dashboardCards.totalPorTipo"
              class="card-content"
              style="border-bottom: 1px solid var(--surface-border); padding: 0.75rem 1rem;"
            >
              <div
                class="card-info"
                style="flex-direction: row; justify-content: space-between; width: 100%; align-items: center;"
              >
                <span class="card-label" style="margin-bottom: 0;">{{
                  tipo.descricao
                }}</span>
                <span class="card-value" style="font-size: 1.25rem;">{{
                  tipo.total
                }}</span>
              </div>
            </div>
          </div>
        </p-card>
      </div>

      <!-- Tabela de Resumo Mensal -->
      <p-card>
        <ng-template pTemplate="header">
          <div class="card-header">
            <h3>Resumo Mensal de Credenciais</h3>
          </div>
        </ng-template>

        <div
          class="table-container"
          *ngIf="
            dashboardSummary && dashboardSummary.length > 0;
            else emptyState
          "
        >
          <table class="data-table">
            <thead>
              <tr>
                <th>Ano</th>
                <th>Mês</th>
                <th>Tipo</th>
                <th class="text-center">TEA</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of dashboardSummary">
                <td>{{ item.ano }}</td>
                <td>{{ getMonthName(item.mes) }}</td>
                <td>{{ item.credentialTypeDescricao }}</td>
                <td class="text-center">
                  <span
                    *ngIf="item.isTea"
                    class="badge"
                    style="background: var(--green-100); color: var(--green-700);"
                    >Sim</span
                  >
                  <span
                    *ngIf="!item.isTea"
                    class="badge"
                    style="background: var(--surface-200); color: var(--text-color-secondary);"
                    >Não</span
                  >
                </td>
                <td class="text-right">
                  <strong>{{ item.total }}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ng-template #emptyState>
          <div class="empty-state">
            <i
              class="pi pi-inbox"
              style="font-size: 3rem; color: var(--text-color-secondary)"
            ></i>
            <p style="color: var(--text-color-secondary); margin: 0;">
              <ng-container *ngIf="loading; else noData">
                <i class="pi pi-spin pi-spinner mr-2"></i>Carregando dados...
              </ng-container>
              <ng-template #noData>
                Nenhum dado encontrado para o período.
              </ng-template>
            </p>
          </div>
        </ng-template>
      </p-card>
    </div>
  `,
  styles: [
    `
      .dashboard-container {
        padding: 1.5rem;
        max-width: 1400px;
        margin: 0 auto;
      }

      .dashboard-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5rem;
      }

      .header-actions {
        display: flex;
        gap: 1rem;
      }

      :host ::ng-deep .filters-card {
        margin-bottom: 2rem;
      }

      .filters-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid var(--surface-border);
      }

      .filters-header h3 {
        color: var(--text-color);
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
      }

      .filters-header i {
        color: var(--text-color-secondary);
        font-size: 1.2rem;
      }

      .filters-container {
        display: flex;
        align-items: flex-end;
        gap: 1.5rem;
        flex-wrap: wrap;
        padding: 1rem 0;
      }

      .filter-group {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        min-width: 140px;
      }

      .filter-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--text-color);
        margin-bottom: 0.5rem;
      }

      /* ============================================ */
      /* ESTILOS GLOBAIS PARA INPUT NUMBER */
      /* ============================================ */

      :host ::ng-deep .ano-input.p-inputnumber,
      :host ::ng-deep .ano-input .p-inputnumber {
        display: inline-flex !important;
        align-items: center !important;
        width: 100% !important;
      }

      :host ::ng-deep .ano-inicio.p-inputnumber,
      :host ::ng-deep .ano-inicio .p-inputnumber,
      :host ::ng-deep .ano-input.p-inputnumber,
      :host ::ng-deep .ano-input .p-inputnumber {
        width: 240px !important;
        min-width: 240px !important;
      }

      /* Input text */
      :host ::ng-deep .ano-input .p-inputtext {
        text-align: center !important;
        font-weight: 500 !important;
        height: 2.5rem !important;
        line-height: 2.5rem !important;
        padding: 0 0.5rem !important;
        font-size: 1rem !important;
        min-height: unset !important;
        max-height: unset !important;
        flex: 1 !important;
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }

      /* Container do grupo de botões - SEM GAP */
      :host ::ng-deep .ano-input .p-inputnumber-button-group {
        display: inline-flex !important;
        flex-direction: column !important;
        height: 2.5rem !important;
        min-height: 2.5rem !important;
        max-height: 2.5rem !important;
        margin-left: 0 !important;
        gap: 0 !important;
      }

      /* Container stacked - SEM GAP */
      :host ::ng-deep .ano-input .p-inputnumber-buttons-stacked {
        display: inline-flex !important;
        flex-direction: column !important;
        height: 2.5rem !important;
        min-height: 2.5rem !important;
        max-height: 2.5rem !important;
        gap: 0 !important;
      }

      /* Botões individuais - COR AZUL e SEM GAP */
      :host ::ng-deep .ano-input .p-inputnumber-button-group .p-button,
      :host ::ng-deep .ano-input .p-inputnumber-buttons-stacked .p-button,
      :host ::ng-deep .ano-input .p-button.p-inputnumber-button {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 1.8rem !important;
        height: 1.25rem !important;
        min-height: 1.25rem !important;
        max-height: 1.25rem !important;
        padding: 0 !important;
        margin: 0 !important;
        background: var(--primary-color) !important;
        border: 1px solid var(--primary-color) !important;
        color: white !important;
        line-height: 1.25rem !important;
        border-radius: 0 !important;
      }

      /* Remove qualquer label dentro do botão */
      :host
        ::ng-deep
        .ano-input
        .p-inputnumber-button-group
        .p-button
        .p-button-label,
      :host
        ::ng-deep
        .ano-input
        .p-inputnumber-buttons-stacked
        .p-button
        .p-button-label {
        display: none !important;
      }

      /* Ícones dentro dos botões - BRANCOS */
      :host
        ::ng-deep
        .ano-input
        .p-inputnumber-button-group
        .p-button
        .p-button-icon,
      :host
        ::ng-deep
        .ano-input
        .p-inputnumber-buttons-stacked
        .p-button
        .p-button-icon {
        font-size: 0.7rem !important;
        margin: 0 !important;
        width: auto !important;
        height: auto !important;
        line-height: 1.25rem !important;
        color: white !important;
      }

      /* ============================================ */
      /* BOTÕES DO ANO INICIAL - BORDAS ESPECÍFICAS */
      /* ============================================ */
      
      /* Primeiro botão (seta para cima) - Ano Inicial */
      :host ::ng-deep .ano-inicio .p-inputnumber-buttons-stacked .p-button:first-child {
        border-radius: 0 4px 0 5px !important;
      }
      
      /* Último botão (seta para baixo) - Ano Inicial */
      :host ::ng-deep .ano-inicio .p-inputnumber-buttons-stacked .p-button:last-child {
        border-radius: 0 0 4px 5px !important;
      }

      /* ============================================ */
      /* BOTÕES DO ANO FINAL - BORDAS ESPECÍFICAS */
      /* ============================================ */
      
      /* Primeiro botão (seta para cima) - Ano Final */
      :host ::ng-deep .ano-final .p-inputnumber-buttons-stacked .p-button:first-child {
        border-radius: 0 4px 0 5px !important;
      }
      
      /* Último botão (seta para baixo) - Ano Final */
      :host ::ng-deep .ano-final .p-inputnumber-buttons-stacked .p-button:last-child {
        border-radius: 0 0 4px 5px !important;
      }

      /* Hover dos botões - tom mais escuro de azul */
      :host ::ng-deep .ano-input .p-inputnumber-button-group .p-button:hover,
      :host
        ::ng-deep
        .ano-input
        .p-inputnumber-buttons-stacked
        .p-button:hover {
        background: var(--primary-600) !important;
        border-color: var(--primary-600) !important;
        color: white !important;
      }

      /* Focus state */
      :host ::ng-deep .ano-input .p-inputtext:focus {
        box-shadow: none !important;
        border-color: var(--primary-color) !important;
      }

      /* ============================================ */
      /* FILTER ACTIONS */
      /* ============================================ */

      .filter-actions {
        display: flex;
        gap: 0.5rem;
        align-items: flex-end;
        margin-left: auto;
        flex: 0 0 auto;
      }

      .filter-actions p-button {
        display: inline-block;
      }

      :host ::ng-deep .filter-actions .p-button {
        height: 2.5rem !important;
        padding: 0 1rem !important;
      }

      /* Botão Filtrar com cor consistente */
      :host ::ng-deep .filter-actions .p-button:first-child {
        background: var(--primary-color) !important;
        border-color: var(--primary-color) !important;
      }

      :host ::ng-deep .filter-actions .p-button:first-child:hover {
        background: var(--primary-600) !important;
        border-color: var(--primary-600) !important;
      }

      /* ============================================ */
      /* RESPONSIVO */
      /* ============================================ */

      @media (min-width: 769px) {
        .filters-container {
          display: flex;
          align-items: flex-end;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .filter-group {
          flex: 0 0 auto;
        }

        .filter-actions {
          flex: 0 0 auto;
          margin-left: auto;
        }
      }

      @media (max-width: 768px) {
        .dashboard-container {
          padding: 1rem;
        }

        .dashboard-header {
          flex-direction: column;
          gap: 1rem;
          align-items: flex-start;
        }

        .dashboard-title {
          font-size: 1.5rem;
        }

        .charts-grid {
          grid-template-columns: 1fr;
        }

        .summary-cards {
          grid-template-columns: 1fr;
        }

        .filters-container {
          flex-direction: column;
          align-items: stretch;
          gap: 1rem;
        }

        .filter-group {
          min-width: 100%;
          margin-bottom: 0;
        }

        :host ::ng-deep .ano-inicio.p-inputnumber,
        :host ::ng-deep .ano-inicio .p-inputnumber,
        :host ::ng-deep .ano-input.p-inputnumber,
        :host ::ng-deep .ano-input .p-inputnumber {
          width: 100% !important;
          min-width: 100% !important;
        }

        .filter-actions {
          margin-top: 0.5rem;
          justify-content: stretch;
          margin-left: 0;
          width: 100%;
        }

        .filter-actions p-button {
          flex: 1;
          width: 100%;
        }

        :host ::ng-deep .filter-actions .p-button {
          width: 100%;
        }

        :host ::ng-deep .ano-input .p-inputtext {
          height: 2rem !important;
          line-height: 2rem !important;
          font-size: 0.875rem !important;
        }

        :host ::ng-deep .ano-input .p-inputnumber-button-group {
          height: 2rem !important;
          min-height: 2rem !important;
          max-height: 2rem !important;
        }

        :host ::ng-deep .ano-input .p-inputnumber-buttons-stacked {
          height: 2rem !important;
          min-height: 2rem !important;
          max-height: 2rem !important;
        }

        :host ::ng-deep .ano-input .p-inputnumber-button-group .p-button,
        :host ::ng-deep .ano-input .p-inputnumber-buttons-stacked .p-button,
        :host ::ng-deep .ano-input .p-button.p-inputnumber-button {
          height: 1rem !important;
          min-height: 1rem !important;
          max-height: 1rem !important;
          width: 1.5rem !important;
        }

        :host
          ::ng-deep
          .ano-input
          .p-inputnumber-button-group
          .p-button
          .p-button-icon,
        :host
          ::ng-deep
          .ano-input
          .p-inputnumber-buttons-stacked
          .p-button
          .p-button-icon {
          font-size: 0.6rem !important;
          line-height: 1rem !important;
        }

        /* Ajuste de bordas responsivo */
        :host ::ng-deep .ano-inicio .p-inputnumber-buttons-stacked .p-button:first-child,
        :host ::ng-deep .ano-final .p-inputnumber-buttons-stacked .p-button:first-child {
          border-radius: 0 4px 0 0 !important;
        }
        
        :host ::ng-deep .ano-inicio .p-inputnumber-buttons-stacked .p-button:last-child,
        :host ::ng-deep .ano-final .p-inputnumber-buttons-stacked .p-button:last-child {
          border-radius: 0 0 4px 0 !important;
        }
      }

      /* ============================================ */
      /* DEMAIS ESTILOS */
      /* ============================================ */

      .periodo-info {
        margin-top: 1rem;
      }

      .periodo-display {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--text-color-secondary);
        font-size: 0.875rem;
      }

      .periodo-range {
        color: var(--text-color-secondary);
        font-style: italic;
      }

      .dashboard-title {
        font-size: 1.75rem;
        font-weight: 600;
        color: var(--text-color);
        margin: 0;
      }

      .empty-dashboard {
        margin-top: 2rem;
      }

      .summary-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 1.5rem;
        margin-bottom: 2rem;
      }

      :host ::ng-deep .summary-card {
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transition:
          transform 0.2s,
          box-shadow 0.2s;
      }

      :host ::ng-deep .summary-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .card-content {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.5rem;
      }

      .card-icon {
        font-size: 2.5rem;
      }

      .card-info {
        display: flex;
        flex-direction: column;
      }

      .card-label {
        font-size: 0.875rem;
        color: var(--text-color-secondary);
        margin-bottom: 0.25rem;
      }

      .card-value {
        font-size: 1.75rem;
        font-weight: 700;
        color: var(--text-color);
      }

      .charts-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
        gap: 1.5rem;
        margin-bottom: 2rem;
      }

      .card-header {
        padding: 1rem 1.5rem;
        border-bottom: 1px solid var(--surface-border);
      }

      .card-header h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--text-color);
      }

      .chart-container {
        padding: 1rem;
      }

      .table-container {
        overflow-x: auto;
      }

      .data-table {
        width: 100%;
        border-collapse: collapse;
      }

      .data-table thead {
        background: var(--surface-100);
      }

      .data-table th {
        padding: 1rem 1.5rem;
        text-align: left;
        font-weight: 600;
        color: var(--text-color);
        border-bottom: 2px solid var(--surface-border);
      }

      .data-table th.text-right {
        text-align: right;
      }

      .data-table th.text-center {
        text-align: center;
      }

      .data-table td {
        padding: 1rem 1.5rem;
        border-bottom: 1px solid var(--surface-border);
        text-align: left;
      }

      .data-table td.text-right {
        text-align: right;
      }

      .data-table td.text-center {
        text-align: center;
      }

      .data-table tbody tr:hover {
        background: var(--surface-hover);
      }

      .text-right {
        text-align: right;
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        text-align: center;
      }

      .empty-state i {
        margin-bottom: 1rem;
      }
    `,
  ],
})
export class DashboardComponent implements OnInit {
  readonly minYear = 2010;

  anoInicio: number = new Date().getFullYear() - 1;
  anoFim: number = new Date().getFullYear();
  activeStartYear: number = this.anoInicio;
  activeEndYear: number = this.anoFim;

  dashboardCards: CredentialDashboardCards | null = null;
  dashboardSummary: CredentialMonthlySummary[] = [];
  loading = false;

  private monthNames = [
    '',
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];

  constructor(
    private credentialsService: CredentialsService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.loadDashboardData();
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  validarAnoInicio(): void {
    if (this.anoInicio && this.anoFim && this.anoInicio > this.anoFim) {
      this.anoFim = this.anoInicio;
    }
  }

  validarAnoFinal(): void {
    if (this.anoInicio && this.anoFim && this.anoFim < this.anoInicio) {
      this.anoFim = this.anoInicio;
    }
  }

  loadDashboardData(): void {
    const validationError = this.validateYearRange(this.anoInicio, this.anoFim);
    if (validationError) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Filtro inválido',
        detail: validationError,
      });
      return;
    }

    this.activeStartYear = this.anoInicio;
    this.activeEndYear = this.anoFim;
    this.loading = true;

    this.credentialsService
      .getCredentialDashboardCards(this.anoInicio, this.anoFim)
      .pipe(
        catchError((error) => {
          console.error('Erro ao buscar cards do dashboard:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao carregar cards do dashboard',
          });
          return of(null);
        }),
      )
      .subscribe((cards) => {
        this.dashboardCards = cards;
      });

    this.credentialsService
      .getCredentialDashboardSummary(this.anoInicio, this.anoFim)
      .pipe(
        catchError((error) => {
          console.error('Erro ao buscar resumo do dashboard:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao carregar resumo mensal do dashboard',
          });
          return of([] as CredentialMonthlySummary[]);
        }),
      )
      .subscribe((summary) => {
        this.dashboardSummary = summary;
        this.loading = false;

        if (!this.dashboardCards && summary.length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Sem dados',
            detail: 'Nenhum dado foi encontrado para o período informado.',
          });
        }
      });
  }

  aplicarFiltros(): void {
    this.loadDashboardData();
  }

  limparFiltros(): void {
    this.anoInicio = new Date().getFullYear() - 1;
    this.anoFim = new Date().getFullYear();
    this.loadDashboardData();
  }

  getPeriodInMonths(): number {
    return (this.activeEndYear - this.activeStartYear + 1) * 12;
  }

  private validateYearRange(startYear: number, endYear: number): string | null {
    if (!Number.isInteger(startYear) || !Number.isInteger(endYear)) {
      return 'Informe anos válidos para o período.';
    }

    if (startYear < this.minYear || endYear > this.getCurrentYear()) {
      return `O período deve estar entre ${this.minYear} e ${this.getCurrentYear()}.`;
    }

    if (startYear > endYear) {
      return 'O ano inicial não pode ser maior que o ano final.';
    }

    return null;
  }

  getMonthName(month: number): string {
    return this.monthNames[month] || `Mês ${month}`;
  }
}